<template>
  <div class="company-form-container">
    <div class="container-title">
      <p>{{ $t('Create a Company') }}</p>
    </div>
    <div class="container-desc">
      <p>{{ $t('Please fill in the company information') }}</p>
    </div>
    <el-form ref="refForm" :model="formData" :rules="formRules" class="form-container" onsubmit="return false" @keyup.enter="submitForm()">
      <el-form-item prop="companyName" class="form-item company-name">
        <el-input name="companyName" v-model="formData.companyName" class="form-item-input" autofocus :placeholder="$t('Company Name')" />
      </el-form-item>

      <el-select v-model="formData.industry" class="trade-select" :placeholder="$t('Industry Type')" filterable clearable>
        <el-option v-for="item in companyIndustryOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select v-model="formData.numberValue" class="number-select" :placeholder="$t('Number of Employees')" @change="numberSplit" clearable>
        <el-option v-for="item in companyUserNumberOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-form-item>
        <el-button class="submit-btn" type="primary" :disabled="formData.companyName.length === 0" @click="submitForm(refForm)" :loading="btnLoading">{{ $t('Continue') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { getCurrentInstance, onMounted, reactive, ref } from 'vue'
import { setSessionStorage, getSessionStorage, setLocalStorage } from '@/libs/utils'
import { useRoute, useRouter } from 'vue-router'
import { getUserInfo } from '@/api/user/user'
import enums from '@/libs/enums'
import regexPattern from '@/libs/regex-patterns.js'
import { ElMessage } from 'element-plus'
import { isLogin } from '@/services/base'
import { appStore, companyStore } from '@/stores'
import { companyIndustryOptions, companyUserNumberOptions } from '@/libs/options'

const $route = useRoute()
const $router = useRouter()
const isCN = appStore.isCN
const formError = ref(false) // 防止有错误时点击提交错误信息会闪两次
const { $t } = getCurrentInstance().proxy

const numberSplit = val => {
  const numberArr = val.split('-')
  formData.employeesMin = parseInt(numberArr[0])
  formData.employeesMax = parseInt(numberArr[1])
}

// 获取客户端传递的token，进行解析，判断是否未个人账号，个人账号则提取邮箱或者手机号进行带入
const formData = reactive({
  companyName: '',
  industry: '',
  numberValue: '',
  employeesMin: 0,
  employeesMax: 0,
  accountType: 'email',
  code: '',
  codeId: '',
})
const refForm = ref(null)
const btnLoading = ref(false)
const formRules = reactive({
  companyName: [
    { required: true, message: $t('Company Name'), trigger: 'blur' },
    isCN
      ? {
          validator: (rule, value, callback) => {
            // 创建企业名称只允许中文
            const testAccount = regexPattern.companyName.test(value)
            if (value === '' || testAccount) {
              formError.value = false
              callback()
            } else {
              formError.value = true
              const errMsg = $t('The company name only supports Chinese characters')
              callback(new Error(errMsg))
            }
          },
          trigger: 'change',
        }
      : {},
  ],
})

const submitForm = () => {
  const activeElement = document.activeElement
  activeElement.blur()
  setTimeout(() => {
    if (!refForm.value) {
      activeElement.focus()
      return
    }
    // 选择使用companyInfo还是companyFormData
    refForm.value.validate(async valid => {
      if (valid && !btnLoading.value) {
        btnLoading.value = true
        if (isCN) {
          if (regexPattern.companyName.test(formData.companyName)) {
            btnLoading.value = false
            Object.assign(companyStore.companyFormData, formData)
            setSessionStorage('companyInfo', JSON.stringify(formData))
            $router.push({ name: 'createPersonalInfo' })
          } else {
            btnLoading.value = false
            ElMessage.error('The company name only supports Chinese characters')
          }
        } else {
          btnLoading.value = false
          Object.assign(companyStore.companyFormData, formData)
          setSessionStorage('companyInfo', JSON.stringify(formData))
          $router.push({ name: 'createPersonalInfo' })
        }
        setTimeout(() => {
          btnLoading.value = false
        }, 3000)
      } else {
        return false
      }
    })
  }, 100)
  return false
}

const handelUserInfo = () => {
  if (isLogin()) {
    getUserInfo(false).then(({ data }) => {
      setSessionStorage('loginUserInfo', JSON.stringify(data.userInfo))
    })
  }
}

onMounted(() => {
  if ($route.query.companyReedemCode) setLocalStorage('companyReedemCode', $route.query.companyReedemCode)
  const data = JSON.parse(getSessionStorage('companyInfo'))
  if (data) {
    formData.companyName = data.companyName
    formData.industry = $t(data.industry)
    formData.numberValue = data.numberValue
  }
  handelUserInfo()
})
</script>
<style lang="scss" scoped>
@use './index.scss';
.form-container {
  .form-item {
    font-size: 14px !important;
    margin-bottom: 24px;
  }
  .el-select {
    margin-bottom: 24px;
  }
}
:deep(.el-select .el-input__wrapper) {
  width: 251px;
  box-sizing: border-box;
  border-radius: inherit;
  box-shadow: 0 0 0 1 var(--el-select-input-focus-border-color) inset !important;
  border-bottom: 1px solid #dadada;
}
:deep(.el-select .el-input.is-focus .el-input__wrapper) {
  border-color: #c08fff;
  box-shadow: none !important;
}
:deep(.el-form-item__error) {
  top: 103% !important;
}
@-moz-document url-prefix() {
  :deep(.el-form-item__error) {
    font-size: 10px;
  }
}
</style>
